const AUDIO_MIME_BY_EXT: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
}

/** 每个 audio 元素当前占用的 blob URL，停止时释放。 */
const audioBlobUrlByElement = new WeakMap<HTMLAudioElement, string>()

/**
 * 当前环境是否需要将远程音频 URL 转为带 MIME 的 blob URL（Safari / iOS WebKit）。
 */
export function needsSafariAudioBlobCompat(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return true
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true
  return /Safari/.test(ua) && !/Chrome|Chromium|CriOS|Edg|OPR|Android/.test(ua)
}

/**
 * 从文件名或 URL 路径推断音频 MIME。
 * @param value 文件名、相对路径或 URL
 */
export function inferAudioMimeFromName(value: string): string | undefined {
  const normalized = String(value || '').trim().split('?')[0]?.split('#')[0] ?? ''
  const ext = normalized.includes('.') ? normalized.split('.').pop()?.toLowerCase() ?? '' : ''
  return ext ? AUDIO_MIME_BY_EXT[ext] : undefined
}

function revokeBlobUrl(url: string): void {
  if (!url.startsWith('blob:')) return
  try {
    URL.revokeObjectURL(url)
  } catch {
    /* ignore invalid object url */
  }
}

function setAudioBlobUrl(audio: HTMLAudioElement, blobUrl: string | null): void {
  const prev = audioBlobUrlByElement.get(audio)
  if (prev && prev !== blobUrl) {
    revokeBlobUrl(prev)
  }
  if (blobUrl?.startsWith('blob:')) {
    audioBlobUrlByElement.set(audio, blobUrl)
    return
  }
  audioBlobUrlByElement.delete(audio)
}

/**
 * 为 Safari 生成可播地址；每次调用都会重新 fetch，不缓存。
 * @param url 资源原始 URL
 * @param mimeHint 可选 MIME
 */
export async function createSafariAudioPlaybackUrl(url: string, mimeHint?: string): Promise<string> {
  const raw = String(url || '').trim()
  if (!raw || !needsSafariAudioBlobCompat()) return raw
  if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw
  if (!/^https?:\/\//i.test(raw)) return raw

  const response = await fetch(raw)
  if (!response.ok) {
    throw new Error(`音频加载失败：HTTP ${response.status}`)
  }
  const buffer = await response.arrayBuffer()
  const mime = mimeHint || inferAudioMimeFromName(raw) || 'audio/mpeg'
  return URL.createObjectURL(new Blob([buffer], { type: mime }))
}

function waitForAudioCanPlay(audio: HTMLAudioElement): Promise<void> {
  if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      audio.removeEventListener('canplay', onCanPlay)
      audio.removeEventListener('error', onError)
    }
    const onCanPlay = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      reject(new Error('音频解码失败'))
    }
    audio.addEventListener('canplay', onCanPlay, { once: true })
    audio.addEventListener('error', onError, { once: true })
  })
}

/**
 * 停止播放并释放该 audio 元素占用的 blob URL。
 */
export function stopHtmlAudio(audio: HTMLAudioElement): void {
  audio.onended = null
  audio.onerror = null
  audio.pause()
  try {
    audio.currentTime = 0
  } catch {
    /* ignore non-seekable audio */
  }
  setAudioBlobUrl(audio, null)
  audio.removeAttribute('src')
  audio.load()
}

/**
 * 加载并播放；播完（非 loop）自动 stop。不保留全局缓存。
 */
export async function playHtmlAudio(
  audio: HTMLAudioElement,
  url: string,
  options?: {
    loop?: boolean
    volume?: number
    mimeHint?: string
    onEnded?: () => void
  },
): Promise<void> {
  stopHtmlAudio(audio)

  const playbackUrl = await createSafariAudioPlaybackUrl(url, options?.mimeHint)
  if (playbackUrl.startsWith('blob:')) {
    setAudioBlobUrl(audio, playbackUrl)
  }

  if (options?.loop !== undefined) audio.loop = options.loop
  if (options?.volume !== undefined) audio.volume = options.volume

  audio.src = playbackUrl
  audio.load()
  await waitForAudioCanPlay(audio)
  try {
    audio.currentTime = 0
  } catch {
    /* ignore non-seekable audio */
  }

  if (!audio.loop) {
    audio.onended = () => {
      stopHtmlAudio(audio)
      options?.onEnded?.()
    }
  }

  await audio.play()
}

/**
 * 测试 rewrite：为音频 MD5 项生成 blob URL，生命周期由 cleanupObjectUrls 管理。
 */
export async function applySafariAudioRewriteForTest(
  map: Record<string, string>,
  audioMd5Keys: string[],
): Promise<{ map: Record<string, string>; cleanupObjectUrls: string[] }> {
  if (!needsSafariAudioBlobCompat() || !audioMd5Keys.length) {
    return { map, cleanupObjectUrls: [] }
  }

  const next = { ...map }
  const cleanupObjectUrls: string[] = []
  const sourceUrlByMd5 = new Map<string, string>()
  for (const md5 of audioMd5Keys) {
    const key = String(md5 || '').trim().toLowerCase()
    const sourceUrl = next[key]
    if (!key || !sourceUrl) continue
    sourceUrlByMd5.set(key, sourceUrl)
  }

  const playbackBySource = new Map<string, string>()
  for (const sourceUrl of new Set(sourceUrlByMd5.values())) {
    if (sourceUrl.startsWith('blob:') || sourceUrl.startsWith('data:')) {
      playbackBySource.set(sourceUrl, sourceUrl)
      continue
    }
    playbackBySource.set(sourceUrl, await createSafariAudioPlaybackUrl(sourceUrl))
  }

  for (const [md5, sourceUrl] of sourceUrlByMd5) {
    const playbackUrl = playbackBySource.get(sourceUrl) ?? sourceUrl
    if (playbackUrl === sourceUrl) continue
    next[md5] = playbackUrl
    if (playbackUrl.startsWith('blob:') && !cleanupObjectUrls.includes(playbackUrl)) {
      cleanupObjectUrls.push(playbackUrl)
    }
  }

  return { map: next, cleanupObjectUrls }
}

/**
 * 释放一组 blob URL（仅用于测试会话 cleanup）。
 */
export function releaseSafariAudioObjectUrls(objectUrls: string[]): void {
  for (const url of objectUrls) {
    revokeBlobUrl(String(url || '').trim())
  }
}
